import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import SEO from "../components/SEO";

function LegalShell({ eyebrow, title, children, seoTitle, seoDesc, path }) {
  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <SEO title={seoTitle || title} description={seoDesc} path={path} />
      <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <p className="eyebrow mb-3">{eyebrow}</p>
        <h1 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7] mb-10">{title}</h1>
        <div className="space-y-6 text-[#FAFAF7]/65 font-light leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

const P = ({ children }) => <p>{children}</p>;
const H = ({ children }) => <h2 className="font-serif-lux text-2xl text-[#FAFAF7] pt-4">{children}</h2>;

export function ShippingReturns() {
  return (
    <LegalShell eyebrow="CUSTOMER SERVICE" title="Shipping & Returns" path="/shipping-returns"
      seoDesc="Queen MJ Maison Beaute shipping and returns — complimentary shipping over $150, express delivery, and 30-day hassle-free returns.">
      <H>Shipping</H>
      <P>We dispatch all orders within 24 hours, Monday to Saturday. Standard delivery arrives in 3–5 business days and is complimentary on orders over $150. Express delivery arrives in 1–2 business days.</P>
      <P>Every order is wrapped in signature Queen MJ Maison Beaute packaging with two complimentary fragrance samples.</P>
      <H>Returns & Exchanges</H>
      <P>If your fragrance isn't quite right, you may return unopened items within 30 days of delivery for a full refund. Simply contact our customer care team and we'll arrange a prepaid return label.</P>
      <P>For hygiene reasons, opened perfumes can only be exchanged if faulty. Candles and air fresheners must be unused and in their original packaging.</P>
      <H>Tracking</H>
      <P>You'll receive a tracking link by email the moment your order ships. You can also view order status any time in your account.</P>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell eyebrow="LEGAL" title="Privacy Policy" path="/privacy"
      seoDesc="How Queen MJ Maison Beaute collects, uses and protects your personal data.">
      <P>Your privacy matters to us. This policy explains what information we collect and how we use it.</P>
      <H>Information We Collect</H>
      <P>We collect the details you provide at checkout and registration — name, email, shipping address and order history — as well as anonymous analytics to improve your experience.</P>
      <H>How We Use It</H>
      <P>We use your information solely to process orders, provide customer support, and — with your consent — send you news about new fragrances. We never sell your data.</P>
      <H>Security</H>
      <P>All transactions are encrypted with 256-bit SSL. Payment details are handled by our PCI-compliant payment partner and are never stored on our servers.</P>
      <H>Your Rights</H>
      <P>You may request access to, correction of, or deletion of your personal data at any time by contacting support@queenmjmaison.store.</P>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell eyebrow="LEGAL" title="Terms & Conditions" path="/terms"
      seoDesc="Terms and conditions for shopping with Queen MJ Maison Beaute.">
      <P>By using www.queenmjmaison.store and placing an order, you agree to the following terms.</P>
      <H>Orders</H>
      <P>All orders are subject to acceptance and availability. Prices are shown in USD and include applicable taxes where required.</P>
      <H>Authenticity</H>
      <P>Every product sold by Queen MJ Maison Beaute is 100% authentic and sourced directly from the maison or authorised distributors.</P>
      <H>Intellectual Property</H>
      <P>All content, imagery and branding on this site is the property of Queen MJ Maison Beaute and may not be reproduced without permission.</P>
      <H>Liability</H>
      <P>Queen MJ Maison Beaute is not liable for any indirect or consequential loss arising from the use of our products beyond the value of the goods purchased.</P>
    </LegalShell>
  );
}

const FAQS = [
  ["Are your fragrances authentic?", "Absolutely. Every product is 100% genuine and sourced directly from the maison or authorised distributors, with our authenticity guarantee on every order."],
  ["How long does shipping take?", "Standard delivery arrives in 3–5 business days (free over $150). Express delivery arrives in 1–2 business days."],
  ["Can I return an item?", "Yes — unopened items can be returned within 30 days for a full refund. Contact our customer care team for a prepaid return label."],
  ["Do you offer samples?", "Every order includes two complimentary fragrance samples, beautifully presented with your purchase."],
  ["Is checkout secure?", "Yes. We use 256-bit SSL encryption and a PCI-compliant payment partner. Your card details never touch our servers."],
  ["Do you ship internationally?", "We ship worldwide. Shipping costs and delivery times are calculated at checkout based on your destination."],
  ["How should I store my perfume?", "Keep your fragrance away from direct sunlight and heat, ideally in its box at room temperature, to preserve its character."],
];

export function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <SEO title="FAQ" path="/faq" description="Frequently asked questions about Queen MJ Maison Beaute — authenticity, shipping, returns, samples and secure checkout."
        jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }} />
      <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <p className="eyebrow mb-3">HELP CENTRE</p>
        <h1 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7] mb-10">Frequently Asked Questions</h1>
        <div className="space-y-3">
          {FAQS.map(([q, a], i) => (
            <div key={i} className="gold-border rounded-xl bg-[#141416] overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between px-5 py-4 text-left">
                <span className="text-[#FAFAF7] font-body">{q}</span>
                <ChevronDown size={18} className={`text-[#D4AF37] transition-transform shrink-0 ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <div className="px-5 pb-5 text-[#FAFAF7]/60 font-light leading-relaxed">{a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
